import { useEffect, useSyncExternalStore } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { trpc } from "../lib/trpc";

type Status = "loading" | "success" | "error";

/**
 * One consume attempt per token per page load. Module-level so it survives the
 * page unmounting and remounting (a magic-link token is single use: a second
 * attempt can only fail and burns the per-IP rate limit).
 */
const attempts = new Map<string, Status>();
const listeners = new Set<() => void>();

function setAttempt(token: string, status: Status) {
  attempts.set(token, status);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test seam: forget finished attempts (a real page load starts with none). */
export function resetMagicLinkAttempts() {
  attempts.clear();
}

/**
 * Magic link callback page
 * Consumes the token once; on failure it stays on a static error state.
 */
export default function MagicLinkCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const token = searchParams.get("token") ?? "";
  const usable = token.length >= 10;

  const attempt = useSyncExternalStore(subscribe, () => attempts.get(token));
  const status: Status = usable ? (attempt ?? "loading") : "error";

  useEffect(() => {
    if (!usable || attempts.has(token)) return;
    setAttempt(token, "loading");
    // Not the mutation hook: its callbacks die with the component, the attempt must not.
    utils.client.auth.consumeMagicLink
      .mutate({ token })
      .then(() => {
        setAttempt(token, "success");
        // The server set the session cookies; a full load picks up the new session.
        window.location.replace("/");
      })
      .catch(() => setAttempt(token, "error"));
  }, [token, usable, utils]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="w-[400px]">
          <CardContent className="pt-6">
            {status === "loading" && (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                <h3 className="font-semibold">Verifying your link</h3>
              </div>
            )}

            {status === "success" && (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-green-700 dark:text-green-300 mx-auto mb-4" />
                <h3 className="font-semibold">Signed in</h3>
              </div>
            )}

            {status === "error" && (
              <div className="text-center py-8">
                <XCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                <h3 className="font-semibold mb-6">Link expired or already used</h3>
                <Button onClick={() => navigate("/login")}>Back to login</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
